import React from 'react';
import { gikClassPrefix } from '@gik/core/constants';
import { FieldState } from 'final-form';
import classnames from 'classnames';
import { FormFieldType } from './types';

export interface FormGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  required?: boolean;
  optional?: boolean;
  requiredAsterisk?: boolean;
  padded?: boolean;
  horizontal?: boolean;
  valid?: boolean;
  fieldAppend?: React.ReactNode;
  label?: React.ReactNode;
  type?: string;
  fieldType?: FormFieldType | string;
  fieldWidth?: string;
  labelWidth?: number;
  labelClassName?: string;
  vertical?: boolean;
  inline?: boolean;
  showError?: boolean;
  center?: boolean;
  fieldState?: Partial<FieldState<unknown>>;
  fieldName?: string;
}

// block name for this component
const blockName = `${gikClassPrefix}-form-group`;

/**
 * GIK FormGroup
 *
 * Displays a label and it's control in either a horizontal or vertical layout. Also displays control validation error messages.
 */
function FormGroup({
  labelWidth,
  children,
  fieldState,
  fieldAppend,
  fieldWidth,
  fieldName,
  label,
  labelClassName,
  vertical,
  fieldType,
  optional,
  required,
  inline,
  showError = true,
  center,
  id,
  className,
  ...otherProps
}: FormGroupProps): React.ReactElement {
  const labelStyle = {
    width: undefined,
  };
  if (labelWidth) labelStyle.width = labelWidth;

  const blockClasses = classnames([
    blockName,
    { [`${blockName}--vertical`]: vertical },
    { [`${blockName}--inline`]: inline },
    { [`${blockName}-${fieldName}`]: fieldName },
    { [`${blockName}--center`]: center },
    { [`${blockName}--error`]: fieldState?.error?.message },
    fieldType ? `${blockName}--${fieldType}` : undefined,
    className || '',
  ]);

  const errorStyle = {
    width: fieldWidth,
  };

  return (
    <div className={blockClasses} {...otherProps} style={errorStyle}>
      {label !== undefined && label !== null && label !== '' && (
        <label className={labelClassName} htmlFor={id} style={labelStyle}>
          {label}
          {required && <span className={`${blockName}__required-indicator`}> *</span>}
          {optional && <span className={`${blockName}__optional-indicator`}> (optional)</span>}
        </label>
      )}
      {inline && ' '}
      <div className={`${blockName}__content-wrapper`}>
        <div className={`${blockName}__content`}>
          <div className={`${blockName}__control`}>{children}</div>
          {showError && fieldState && fieldState.error && fieldState.touched && (
            <div className={`${blockName}__error`}>{fieldState.error.message}</div>
          )}
        </div>
        {fieldAppend}
      </div>
    </div>
  );
}

export default FormGroup;
